import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { font, Button, Input, Panel, Avatar, Badge, EmptyState, Tabs } from "@realtyflows/ui";
import { useTheme } from "../contexts/ThemeContext";
import { getClients, createClient, deleteClient, updateEngagementPattern, logClientActivity } from "../api";
import ScoreBadge from "../components/ScoreBadge";
import ConfirmModal from "../components/ConfirmModal";
import Alert from "../components/Alert";
import AiEmailDraftModal from "../components/AiEmailDraftModal";
import ImportClientsModal from "../components/ImportClientsModal";
import useResponsive from "../hooks/useResponsive";

export default function Clients({ workspace }) {
  // V2 ADR-003 Phase 4.3 wave 6 — Clients page reads tokens via useTheme()
  // so per-portal `primary_color` paints intent badges, engagement chips,
  // hot-score outlines, and the "+ Add Client" CTA accents.
  const t = useTheme();
  const navigate = useNavigate();
  const responsive = useResponsive();
  const isMobile = responsive.width < 640;
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", email: "", phone: "", budget: "", location: "", timeline: "", source: "Manual", notes: "" });
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [quickLogToast, setQuickLogToast] = useState(""); // success message after logging
  const [formError, setFormError] = useState("");
  // V2 Feature 2.7: AI email draft modal — opened from the per-client menu
  const [aiDraftFor, setAiDraftFor] = useState(null); // clientId or null
  // V1 CSV import — closes the long-promised "I want to bulk-load my book of business" gap
  const [showImport, setShowImport] = useState(false);

  /**
   * Quick-log an activity for a client straight from the client list — no profile open.
   * V1 feature: speeds up the agent's daily after-call/after-text flow.
   */
  const handleQuickLog = async (clientId, type) => {
    setOpenMenu(null);
    try {
      await logClientActivity(clientId, { type });
      const verb = { call: "Call", text: "Text", email: "Email", showing: "Showing", note: "Note" }[type] || "Activity";
      setQuickLogToast(`${verb} logged`);
      setTimeout(() => setQuickLogToast(""), 2200);
      // Refresh so any score/last_activity updates show up.
      fetchClients();
    } catch (err) {
      setQuickLogToast(`Couldn't log: ${err.message || "error"}`);
      setTimeout(() => setQuickLogToast(""), 3500);
    }
  };

  // Reusable list of quick-log options (used by both mobile + desktop dropdowns)
  const QUICK_LOG_OPTIONS = [
    { type: "call",    label: "Log Call" },
    { type: "text",    label: "Log Text" },
    { type: "email",   label: "Log Email" },
    { type: "showing", label: "Log Showing" },
    { type: "note",    label: "Add Note" },
  ];

  const patternLabels = {
    ghost_post_showing: "Ghost (Post-Showing)",
    ghost_pre_showing: "No-Show",
    ghost_post_offer: "Ghost (Post-Offer)",
    seasonal_pauser: "Seasonal",
    slow_burner: "Slow Burner",
    referred_out: "Referred Out",
    active: "Active",
  };
  const patternColors = {
    ghost_post_showing: t.coral, ghost_pre_showing: t.coral, ghost_post_offer: "#e74c3c",
    seasonal_pauser: t.amber, slow_burner: t.sky, referred_out: t.textMuted, active: t.emerald,
  };

  const fetchClients = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (filter === "High Intent") params.intent = "High";
    if (filter === "New") params.status = "New Client";
    if (filter === "Contacted") params.status = "Contacted";
    if (filter === "Ghosts") params.pattern = "ghost_post_showing";
    if (filter === "No-Shows") params.pattern = "ghost_pre_showing";
    if (filter === "Referred Out") params.pattern = "referred_out";
    if (workspace) params.workspace = workspace;
    getClients(params).then(setClients).catch(console.error).finally(() => setLoading(false));
  }, [filter, search, workspace]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const handleCreate = async () => {
    if (!newClient.name.trim()) return;
    setFormError("");
    try { await createClient(newClient); setNewClient({ name: "", email: "", phone: "", budget: "", location: "", timeline: "", source: "Manual", notes: "" }); setShowAddForm(false); fetchClients(); } catch (err) { setFormError(err.message || "Failed to create client"); }
  };

  const handleDelete = async (id) => {
    try { await deleteClient(id); fetchClients(); } catch (err) { console.error(err); }
  };

  if (loading) return <div style={{ color: t.textMuted, padding: 40, textAlign: "center", fontFamily: font.body }}>Loading clients...</div>;

  return (
    <div style={{ animation: "fadeSlide 0.3s ease" }}>
      <div>
        <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 10, marginBottom: 16, alignItems: isMobile ? "stretch" : "center" }}>
          <div style={{ flex: isMobile ? undefined : 1 }}>
            <Input placeholder="Search clients..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div style={isMobile ? { overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none" } : undefined}>
            <Tabs
              tabs={["All", "High Intent", "New", "Contacted", "Ghosts", "No-Shows", "Referred Out"]}
              activeKey={filter}
              onChange={setFilter}
              variant="pill"
              size="sm"
            />
          </div>
          <Button variant="ghost" size="sm" onClick={() => setShowImport(true)} style={isMobile ? { width: "100%" } : undefined}>Import CSV</Button>
          <Button variant={showAddForm ? "primary" : "outline"} size="sm" onClick={() => { setShowAddForm(!showAddForm); setFormError(""); }} style={isMobile ? { width: "100%" } : undefined}>+ Add Client</Button>
        </div>

        {showAddForm && (
          <div style={{ background: t.surface, border: `1.5px solid ${t.emerald}30`, borderRadius: 12, padding: 20, marginBottom: 16, animation: "scaleIn 0.2s ease" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 16, fontFamily: font.body }}>Add New Client</div>
            <Alert message={formError} onDismiss={() => setFormError("")} style={{ marginBottom: 14 }} />
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
              {[
                { key: "name", label: "Full Name", placeholder: "John Smith" },
                { key: "email", label: "Email", placeholder: "john@example.com" },
                { key: "phone", label: "Phone", placeholder: "(312) 555-0000" },
                { key: "location", label: "Preferred Area", placeholder: "Lincoln Park" },
              ].map(field => (
                <Input key={field.key} label={field.label} placeholder={field.placeholder} value={newClient[field.key]}
                  onChange={e => setNewClient({ ...newClient, [field.key]: e.target.value })} />
              ))}
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 4, fontWeight: 600, fontFamily: font.body }}>Source</div>
              <div style={{ display: "flex", gap: 8, flexWrap: isMobile ? "wrap" : "nowrap" }}>
                {["Website", "Zillow", "Facebook Ad", "Instagram", "Referral", "Manual"].map(src => (
                  <div key={src} onClick={() => setNewClient({ ...newClient, source: src })}
                    style={{ padding: "6px 12px", borderRadius: 6, background: newClient.source === src ? t.emeraldLight : t.surfaceWarm, border: `1px solid ${newClient.source === src ? t.emerald : t.border}`, fontSize: 11, color: newClient.source === src ? t.emerald : t.textSecondary, cursor: "pointer", fontFamily: font.body }}>{src}</div>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <Button variant="primary" size="sm" onClick={handleCreate}>Save Client</Button>
              <Button variant="secondary" size="sm" onClick={() => setShowAddForm(false)}>Cancel</Button>
            </div>
          </div>
        )}

        <Panel padding={0}>
          {!responsive.isCompact && (
            <div style={{ display: "grid", gridTemplateColumns: "2fr 0.9fr 0.9fr 0.7fr 0.7fr 1fr 0.8fr 36px", padding: "10px 16px", borderBottom: `1.5px solid ${t.border}`, fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: 1, fontFamily: font.mono }}>
              <div>CONTACT</div><div>SOURCE</div><div>LOCATION</div><div>INTENT</div><div>SCORE</div><div>STATUS</div><div>AGENT</div><div></div>
            </div>
          )}
          {clients.map(client => (
            responsive.isCompact ? (
              /* Card layout for compact / mobile */
              <div key={client.id} onClick={() => navigate(`/clients/detail?clientId=${client.id}`)}
                style={{ padding: isMobile ? "12px 12px" : "12px 16px", borderBottom: `1px solid ${t.border}`, cursor: "pointer", transition: "background 0.15s",
                  opacity: client.engagement_pattern === "referred_out" ? 0.5 : 1 }}
                onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <Avatar name={client.name} size={32} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{client.name}</div>
                    <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{client.email}</div>
                  </div>
                  {!isMobile && (
                    <div style={{ fontSize: 11, color: client.assigned_name ? t.emerald : t.borderDark, fontFamily: font.body, flexShrink: 0 }}>
                      {client.assigned_name || "\u2014"}
                    </div>
                  )}
                  <div style={{ position: "relative", flexShrink: 0 }}>
                    <div onClick={e => { e.stopPropagation(); setOpenMenu(openMenu === client.id ? null : client.id); }}
                      style={{ width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6, cursor: "pointer", background: openMenu === client.id ? t.surfaceWarm : "transparent", transition: "background 0.15s" }}>
                      <svg width="14" height="14" viewBox="0 0 16 16" fill={t.textMuted}><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                    </div>
                    {openMenu === client.id && (
                      <>
                        <div onClick={e => { e.stopPropagation(); setOpenMenu(null); }} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }} />
                        <div style={{ position: "absolute", right: 0, top: 32, background: t.surface, border: `1px solid ${t.border}`, borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.15)", zIndex: 100, minWidth: 170, overflow: "hidden" }}>
                          <div style={{ padding: "8px 14px 4px", fontSize: 9, color: t.textMuted, fontFamily: font.mono, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Quick Log</div>
                          {QUICK_LOG_OPTIONS.map(opt => (
                            <div key={opt.type} onClick={e => { e.stopPropagation(); handleQuickLog(client.id, opt.type); }}
                              style={{ padding: "8px 14px", fontSize: 12, color: t.textPrimary, cursor: "pointer", fontFamily: font.body }}
                              onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                              {opt.label}
                            </div>
                          ))}
                          <div style={{ height: 1, background: t.border }} />
                          {/* V2 Feature 2.7: AI email draft entry point */}
                          <div onClick={e => { e.stopPropagation(); setOpenMenu(null); setAiDraftFor(client.id); }}
                            style={{ padding: "10px 14px", fontSize: 12, color: t.textPrimary, cursor: "pointer", fontFamily: font.body, display: "flex", alignItems: "center", gap: 8 }}
                            onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            ✨ Draft email with AI
                          </div>
                          <div style={{ height: 1, background: t.border }} />
                          <div onClick={e => { e.stopPropagation(); setOpenMenu(null); navigate(`/clients/detail?clientId=${client.id}&edit=true`); }}
                            style={{ padding: "10px 14px", fontSize: 12, color: t.textPrimary, cursor: "pointer", fontFamily: font.body, display: "flex", alignItems: "center", gap: 8 }}
                            onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            Edit Client
                          </div>
                          <div style={{ height: 1, background: t.border }} />
                          <div onClick={e => { e.stopPropagation(); setOpenMenu(null); setConfirmDelete(client); }}
                            style={{ padding: "10px 14px", fontSize: 12, color: "#e74c3c", cursor: "pointer", fontFamily: font.body, display: "flex", alignItems: "center", gap: 8 }}
                            onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" strokeWidth="2"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                            Delete Client
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                  <div style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: t.surfaceWarm, color: t.textSecondary, fontFamily: font.mono }}>{client.source}</div>
                  {client.location && <div style={{ fontSize: 11, color: t.textSecondary, fontFamily: font.body }}>{client.location}</div>}
                  <Badge variant={client.intent === "High" ? "emerald" : client.intent === "Medium" ? "amber" : "default"}>{client.intent}</Badge>
                  <ScoreBadge score={client.score} />
                  <div style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, fontWeight: 600, fontFamily: font.mono,
                    background: (patternColors[client.engagement_pattern] || t.emerald) + "15",
                    color: patternColors[client.engagement_pattern] || t.emerald,
                    border: `1px solid ${(patternColors[client.engagement_pattern] || t.emerald)}30`,
                  }}>
                    {patternLabels[client.engagement_pattern] || "Active"}
                  </div>
                </div>
              </div>
            ) : (
              /* Desktop grid layout */
              <div key={client.id} onClick={() => navigate(`/clients/detail?clientId=${client.id}`)}
                style={{ display: "grid", gridTemplateColumns: "2fr 0.9fr 0.9fr 0.7fr 0.7fr 1fr 0.8fr 36px", padding: "12px 16px", borderBottom: `1px solid ${t.border}`, alignItems: "center", cursor: "pointer", transition: "background 0.15s",
                  opacity: client.engagement_pattern === "referred_out" ? 0.5 : 1 }}
                onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Avatar name={client.name} size={32} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{client.name}</div>
                    <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>{client.email}</div>
                  </div>
                </div>
                <div style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: t.surfaceWarm, color: t.textSecondary, fontFamily: font.mono, width: "fit-content" }}>{client.source}</div>
                <div style={{ fontSize: 12, color: t.textSecondary, fontFamily: font.body }}>{client.location}</div>
                <Badge variant={client.intent === "High" ? "emerald" : client.intent === "Medium" ? "amber" : "default"}>{client.intent}</Badge>
                <ScoreBadge score={client.score} />
                <div style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, fontWeight: 600, fontFamily: font.mono, width: "fit-content",
                  background: (patternColors[client.engagement_pattern] || t.emerald) + "15",
                  color: patternColors[client.engagement_pattern] || t.emerald,
                  border: `1px solid ${(patternColors[client.engagement_pattern] || t.emerald)}30`,
                }}>
                  {patternLabels[client.engagement_pattern] || "Active"}
                </div>
                <div style={{ fontSize: 11, color: client.assigned_name ? t.emerald : t.borderDark, fontFamily: font.body }}>
                  {client.assigned_name || "\u2014"}
                </div>
                <div style={{ position: "relative" }}>
                  <div onClick={e => { e.stopPropagation(); setOpenMenu(openMenu === client.id ? null : client.id); }}
                    style={{ width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6, cursor: "pointer", background: openMenu === client.id ? t.surfaceWarm : "transparent", transition: "background 0.15s" }}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill={t.textMuted}><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
                  </div>
                  {openMenu === client.id && (
                    <>
                      <div onClick={e => { e.stopPropagation(); setOpenMenu(null); }} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }} />
                      <div style={{ position: "absolute", right: 0, top: 32, background: t.surface, border: `1px solid ${t.border}`, borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.15)", zIndex: 100, minWidth: 170, overflow: "hidden" }}>
                        <div style={{ padding: "8px 14px 4px", fontSize: 9, color: t.textMuted, fontFamily: font.mono, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Quick Log</div>
                        {QUICK_LOG_OPTIONS.map(opt => (
                          <div key={opt.type} onClick={e => { e.stopPropagation(); handleQuickLog(client.id, opt.type); }}
                            style={{ padding: "8px 14px", fontSize: 12, color: t.textPrimary, cursor: "pointer", fontFamily: font.body }}
                            onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            {opt.label}
                          </div>
                        ))}
                        <div style={{ height: 1, background: t.border }} />
                        <div onClick={e => { e.stopPropagation(); setOpenMenu(null); navigate(`/clients/detail?clientId=${client.id}&edit=true`); }}
                          style={{ padding: "10px 14px", fontSize: 12, color: t.textPrimary, cursor: "pointer", fontFamily: font.body, display: "flex", alignItems: "center", gap: 8 }}
                          onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          Edit Client
                        </div>
                        <div style={{ height: 1, background: t.border }} />
                        <div onClick={e => { e.stopPropagation(); setOpenMenu(null); setConfirmDelete(client); }}
                          style={{ padding: "10px 14px", fontSize: 12, color: "#e74c3c", cursor: "pointer", fontFamily: font.body, display: "flex", alignItems: "center", gap: 8 }}
                          onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" strokeWidth="2"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                          Delete Client
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )
          ))}
          {clients.length === 0 && <EmptyState title="No clients found" sub="Try adjusting your filters or add a new client" />}
        </Panel>
      </div>

      {confirmDelete && (
        <ConfirmModal
          title="Delete Contact"
          message={`Are you sure you want to delete "${confirmDelete.name}"? This will also delete all their deals. This action cannot be undone.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => { handleDelete(confirmDelete.id); setConfirmDelete(null); }}
        />
      )}

      {/* V2 Feature 2.7: AI email draft modal */}
      {aiDraftFor && (
        <AiEmailDraftModal
          clientId={aiDraftFor}
          intent="follow_up"
          onClose={() => setAiDraftFor(null)}
          onSent={() => { setAiDraftFor(null); setQuickLogToast("Email sent"); setTimeout(() => setQuickLogToast(""), 2200); }}
        />
      )}

      {/* V1 CSV import modal */}
      <ImportClientsModal
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => { fetchClients(); }}
      />


      {/* Quick-log confirmation toast (V1) */}
      {quickLogToast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: t.textPrimary, color: t.surface,
          padding: "10px 18px", borderRadius: 8, fontSize: 13, fontFamily: font.body,
          boxShadow: "0 6px 20px rgba(0,0,0,0.2)", zIndex: 200,
          animation: "fadeSlide 0.2s ease",
        }}>
          {quickLogToast}
        </div>
      )}
    </div>
  );
}
